import React, {useCallback, useEffect, useRef, useState} from "react";
import {Avatar, Box, Container, Grid, Link, Skeleton, Typography, useMediaQuery, useTheme} from "@mui/material";
import PropTypes from "prop-types";
import PlayerLib from "../../libs/player";
import ClientConfigurationHelper from "../../configuration/ClientConfigurationHelper";
import Analytics from "../../libs/analytics";
import MediaDetails from "./MediaDetails";
import ProductsVertical from "./ProductsVertical";
import ProductsHorizontal from "./ProductsHorizontal";
import AmbassadorHelper from "../../utils/AmbassadorHelper";
import common from "../../utils/common";

const sourceAspectRatio = content => {
    const {asset} = content;
    if (content.entityType === 1) {
        const {width, height} = asset.sources[0];
        return height / width;
    } else {
        const sourceImage = asset.sources && asset.sources ? asset.sources[0] : {}
        return sourceImage.height / sourceImage.width;
    }
}

const calculateAspectRatio = content => {
    const aspectRatio = sourceAspectRatio(content);
    if (aspectRatio <= 0.5631)
        return "16:9";
    else if (aspectRatio > 0.5631 && aspectRatio < 0.76)
        return "4:3";
    else if (aspectRatio >= 0.76 && aspectRatio <= 1)
        return "1:1";
    else if (aspectRatio > 1)
        return "9:16";
    else
        return "16:9";

}

const aspectRatioToPaddingTop = aspectRatio => {
    switch (aspectRatio) {
        case "16:9":
            return "56.25%";
        case "4:3":
            return "75%";
        case "1:1":
            return "100%";
        case "9:16":
            return "177.7777%";
        default:
            return "56.25%"
    }
}
// portrait 9:16 325px desktop
// 4:3 770px desktop
// 16:9 577px
// 1:1 577px

const getWidth = (aspectRatio) => {
    switch (aspectRatio) {
        case "56.25%":
        case "16:9":
            return "1026px";
        case "4:3":
        case "75%":
            return "770px";
        case "1:1":
        case "100%":
            return "636px";
        case "133%":
        case "186%":
        case "177.7777%":
        case "9:16":
            return "357px";
        default:
            return "636px"
    }
}

const isPortrait = (aspectRatio) => {
    switch (aspectRatio) {
        case "133%":
        case "186%":
        case "177.7777%":
        case "9:16":
            return true;
        default:
            return false
    }
}

const isLandscape = (aspectRatio) => {
    switch (aspectRatio) {
        case "56.25%":
        case "16:9":
            return true;
        default:
            return false
    }
}


let player = null;

const getPhotoSource = content => (content.asset && content.asset.sources ? content.asset.sources[0].url : "")

const MediaView = ({products, content, enableStorefront}) => {
    const playerRef = useRef(null);
    const [initialized, setInitialized] = useState(false);
    const [playerReady, setPlayerReady] = useState(false);
    const [width, setWidth] = useState('100%');
    const [height, setHeight] = useState('100%');
    const theme = useTheme();
    const mobileView = useMediaQuery(theme.breakpoints.down('sm'));
    const isVideo = content.entityType === 1;
    const photoStyles = {};
    if (!isVideo) {
        photoStyles.backgroundPosition = 'center';
        photoStyles.backgroundSize = 'cover';
        photoStyles.backgroundRepeat = 'no-repeat';
        photoStyles.backgroundImage = `url(${getPhotoSource(content)})`;

    } else {
        photoStyles["& > div"] = {
            top: 0,
            width: !mobileView && isPortrait(calculateAspectRatio(content)) ? `${getWidth(calculateAspectRatio(content))} !important` : 'auto',
            height: !mobileView && isPortrait(calculateAspectRatio(content)) ? '634px !important' : 'auto'
        }
    }
    const mediaWidth = useCallback(() => {
        const el = document.getElementById('media-container');
        const playerEl = document.getElementById('tvpage-player');
        if (playerEl) {
            setHeight(playerEl.clientHeight);
        }
        if (mobileView) {
            setWidth('100%');
            return;
        }
        if (el) {
            setWidth(el.firstChild.clientWidth + el.lastChild.clientWidth);
            return;
        }
        setWidth('auto');

    }, [setWidth, mobileView]);

    useEffect(() => {
        if (!isVideo) return;
        let observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (
                        entry.intersectionRatio !== 1 &&
                        !player?.instance?.player?.paused()
                    ) {
                        player?.pause();
                    } else if (player?.instance?.player?.paused() && entry.intersectionRatio === 1) {
                        player?.instance?.player?.play();
                    }
                });
            },
            {threshold: 1}
        );
        if (playerRef.current && player) {
            observer?.observe(playerRef.current);
        }
        return () => {
            if (playerRef.current && player) {
                observer?.unobserve(playerRef.current);
            }
        }
    }, [playerRef, player, isVideo])

    useEffect(() => {
        if (!initialized) {
            setInitialized(true);
            mediaWidth();
            window.addEventListener('resize', mediaWidth);

            const options = {
                play_button_border_radius: '50%',
                play_button_border_width: '5px',
                play_button_border_color: theme.play_icon_color,
                play_button_border_style: 'solid',
                play_button_background_color: theme.play_icon_color,
                overlay_opacity: 1,
                poster: true,
                progress_color: theme.player_progressbar_color,
                api_base_url: ClientConfigurationHelper.getGlobalConfiguration().apiUrl,
                data: [content],
                analytics: true,
                autoplay: true,
                autonext: true,
                fill: true,
                onPlayerReady: () => {
                    console.log("player ready", playerReady);
                    setPlayerReady(true);
                },
                loginId: ClientConfigurationHelper.getGlobalConfiguration().loginId //aspectRatio: calculateAspectRatio(content)
            };
            const globalConfig = {}
            if (isVideo) {
                player = new PlayerLib('tvpage-player', options, globalConfig);
            } else {
                Analytics.photoView(content);
            }

        }
        return () => {
            window.removeEventListener('resize', mediaWidth);
        }
    }, [initialized, setInitialized])

    // portrait 9:16 325px desktop
    // 4:3 770px desktop
    // 16:9 577px
    // 1:1 577px

    const {owner} = window.TVSite.channelVideosData.asset;
    const ambassadorAvatar = owner && owner.data ? AmbassadorHelper.getAmbassadorPhotoFromApi({...owner.data, id: owner.id}) : undefined;
    const ambassadorLink = owner && owner.data ? common.generateAmbassadorUrl({...owner.data, id: owner.id}) : '#';
    const AmbassadorComponent = enableStorefront ? Link : Box;
    return (
        <>
            <Container>
                <AmbassadorComponent
                    href={ambassadorLink}
                    underline={"none"}
                    color={"#000"}
                    sx={{ position: "relative" }}
                >
                    <Box display={"flex"} flexGrow={1} alignItems="center" flexWrap={"nowrap"}>
                        <Box sx={{ marginRight: (theme) => theme.spacing(1) }}>
                            <Avatar
                                src={ambassadorAvatar}
                                sx={{ width: "32px", height: "32px" }}
                                alt={
                                    owner && owner.data
                                        ? AmbassadorHelper.getDisplayNameFromApi({
                                              ...owner.data,
                                              first_name: owner.first_name,
                                              last_name: owner.last_name,
                                          })
                                        : ""
                                }
                            />
                        </Box>
                        <Typography
                            sx={{ color: "#000000" }}
                            fontWeight={700}
                            lineHeight={"30px"}
                            variant={"h6"}
                        >
                            {owner && owner.data ? (
                                AmbassadorHelper.getDisplayNameFromApi({
                                    ...owner.data,
                                    first_name: owner.first_name,
                                    last_name: owner.last_name,
                                })
                            ) : (
                                <Skeleton
                                    width={"100%"}
                                    sx={{ height: (theme) => theme.spacing(3) }}
                                    animation={"wave"}
                                />
                            )}
                        </Typography>
                    </Box>
                </AmbassadorComponent>
            </Container>
            <Container sx={{ py: 2 }} disableGutters={mobileView}>
                <Box
                    display={"flex"}
                    width={"100%"}
                    flexWrap={mobileView ? "wrap" : "nowrap"}
                    justifyContent={"center"}
                    flexDirection={"column"}
                >
                    <Grid
                        container
                        display={"flex"}
                        id={"media-container"}
                        justifyContent={"center"}
                    >
                        <Grid
                            xs={12}
                            sm={isLandscape(calculateAspectRatio(content)) ? 9 : "auto"}
                            md={isLandscape(calculateAspectRatio(content)) ? 9 : "auto"}
                            lg={isLandscape(calculateAspectRatio(content)) ? 10 : "auto"}
                            item
                            position={"relative"}
                            width={mobileView ? "100%" : "auto"}
                        >
                            <Box
                                id="tvpage-player"
                                ref={playerRef}
                                paddingTop={aspectRatioToPaddingTop(calculateAspectRatio(content))}
                                sx={photoStyles}
                                width={
                                    mobileView ? "100%" : getWidth(calculateAspectRatio(content))
                                }
                                height={
                                    mobileView && isPortrait(calculateAspectRatio(content))
                                        ? "570px"
                                        : "auto"
                                }
                            >
                                {isVideo && !playerReady && (
                                    <Skeleton
                                        width={"100%"}
                                        height={"100%"}
                                        sx={{ position: "absolute", top: 0 }}
                                        variant={"rectangular"}
                                        animation={"wave"}
                                    />
                                )}
                            </Box>
                        </Grid>
                        {!mobileView && (
                            <Grid
                                item
                                sm={3}
                                md={3}
                                lg={2}
                                bgcolor={"#f8f8f8"}
                                flexDirection={"column"}
                            >
                                <ProductsVertical
                                    height={!mobileView ? height : "auto"}
                                    products={products}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </Container>
            <Container>
                <Box display={"flex"} justifyContent={"center"}>
                    <MediaDetails width={width} qtyProducts={0} content={content} />
                </Box>
            </Container>
            {mobileView && (
                <Container sx={{ backgroundColor: "#f8f8f8", mt: 2 }}>
                    <ProductsHorizontal products={products} />
                </Container>
            )}
        </>
    );
}
MediaView.defaultProps = {
    enableStorefront: false
}
MediaView.propTypes = {
    content: PropTypes.object.isRequired
}

export default MediaView;
